<template>
  <!-- for box-shadow to appear when stacking multiple notifcations -->
  <div
    class="aha-invitation-notification"
  >
    <notification-with-actions
      v-for="(invitation, index) in invitations"
      :z-index="index"
      :key="invitation.id"
      :handleAccept="
        () => handleinvitationsAction('accept', invitation.id)
      "
      :handleDecline="() => handleClickDecline(invitation)"
      :loading="processing"
    >
      <span v-html="getInvitationMessage(invitation)"></span>
    </notification-with-actions>
    <modal-decline-invitation
      @hide="showingModalDecline = false"
      v-if="showingModalDecline"
      :content="getDeclineContentMessage(activeInvitation)"
      :handleDecline="
        () => handleinvitationsAction('decline', activeInvitation.id)"
    />
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { mapState, mapActions } from 'vuex'
import constants from '@/constant'
import InvitationApi from '@/api/invitation'
import Util from '@/util'
import NotificationWithActions from './NotificationWithActions.vue'
import ModalDeclineInvitation from '../../modals/DeclineInvitation.vue'
import eventCategories from '@/constant/eventCategories'
import config from '@/config'

export default {
  components: {
    NotificationWithActions,
    ModalDeclineInvitation,
  },
  props: {
    handleAccept: {
      type: Function,
      default: () => {},
    },
    handleDecline: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      constants,
      showingModalDecline: false,
      activeInvitation: null,
      invitations: [],
      processing: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      hasOnboardingV3: (state) => state.indicators.hasOnboardingV3,
    }),
  },
  mounted() {
    if (!this.currentUser.accountId) { // only for users without team
      this.getInvitations()
    }
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'getCurrentTeamAction']),
    getInvitationMessage(invitation) {
      const text = this.$i18nPresenter.t('has invited you to join team')
      const name = `${invitation.user.firstName} ${invitation.user.lastName}`
      return `<strong>${(invitation.user && `${Util.sanitize(name)}`) || ''}` +
        ` (${invitation.user && invitation.user.email})</strong> ${text} <strong>${invitation.account && invitation.account.name}</strong>`
    },
    getDeclineContentMessage(invitation) {
      if (!invitation) return ''
      const textAreYouSure = this.$i18nPresenter.t('Are you sure you want to decline the invitation from')
      const textToJoin = this.$i18nPresenter.t('to join')
      return `${textAreYouSure} <strong>${(invitation.user && `${Util.sanitize(invitation.user.firstName)} ${Util.sanitize(invitation.user.lastName)}`) || ''}` +
        ` (${invitation.user && invitation.user.email})</strong> ${textToJoin} <strong>${invitation.account && Util.sanitize(invitation.account.name)}</strong>?`
    },
    handleClickDecline(invitation) {
      this.activeInvitation = invitation
      this.showingModalDecline = true
      const eventLabel = this.activeInvitation.account.name || ''
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_LIST,
        'Team > Refuse invitation',
        eventLabel,
        '',
        {
          plan: this.currentUser.plan,
        },
      )
    },
    async getInvitations() {
      try {
        this.invitations = await InvitationApi.getGlobalInvitationNotifications({
          invitedEmail: this.currentUser.email,
          status: constants.invitationStatus.PENDING,
        })
      } catch (e) {
        Sentry.captureException(e)
      }
    },
    async handleinvitationsAction(action, invitationId) {
      const status = action === 'accept' ? 'accepted' : 'declined'
      const index = this.invitations.findIndex((n) => n.id === invitationId)
      this.processing = true
      try {
        if (action === 'accept') {
          // hide all except the currently accepted invitation
          this.invitations = [this.invitations[index]]
          const evtLabel = this.invitations[0].account.name || ''
          await InvitationApi.acceptInvitation(invitationId)
          this.invitations = [] // once accepted an invitation, discard all other invitations
          Util.mixpanelTracking(
            eventCategories.PRESENTATION_LIST,
            'Team > Accept invitation',
            evtLabel,
            '',
            {
              plan: this.currentUser.plan,
            },
          )
          window.location.href = `${config.presenterDomain}/apps/team/members`
        } else if (action === 'decline') {
          // Team > Refuse invitation
          await InvitationApi.declineInvitation(invitationId)
          this.$set(this.invitations[index], 'status', status)
          this.invitations = this.invitations
            .filter((ivt) => ivt.status === constants.invitationStatus.PENDING)
          this.invitations = [this.invitations[index]]
          window.localStorage.setItem(`${constants.workspace.WORKING_MODE}-${this.currentUser.id}`,
            constants.workspace.PERSONAL)
          this.$store.state.workingMode = constants.workspace.PERSONAL
          window.location.reload()
        }
        const toastMsg = this.$i18nPresenter.t(`Invitation ${status} successfully`)
        Util.showToastSuccess(toastMsg)
      } catch (e) {
        if (e.body && e.body.message) {
          this.setErrorOnInternalServerAction(e.body.message)
        } else {
          const internalMsg = this.$i18nPresenter.t('There is a problem with your invitation')
          this.setErrorOnInternalServerAction(internalMsg)
        }
        Sentry.captureException(e)
        this.processing = false
        this.getInvitations()
      }
      return null
    },
  },
}
</script>

<style lang="scss" scoped>
.aha-invitation-notification {
  @apply relative z-0;
}
</style>
